import React, {useMemo, useState} from "react";
import * as THREE from "three";
import Layer from "./Layer";
import {COLORS} from "../../../../../../Constants";
import LineElement from "./LineElement";
import Cut from "./Cut";
import {withStopPropagation} from "../../../../../../utils/common/StopPropagation";

/** Hit strip width in detail mm — Line2 alone is too thin after orthographic zoom. */
const EDGE_HIT_WIDTH_MM = 10;
/** Keep endpoints free so Point clicks still open the toolbar popover. */
const EDGE_HIT_ENDPOINT_GAP_MM = 8;
const ARC_HIT_STEP = 20;

const buildHitBoxes = (points) => {
    if (!points || points.length < 2) {
        return [];
    }
    const boxes = [];
    for (let i = 0; i < points.length - 1; i++) {
        const a = points[i];
        const b = points[i + 1];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const length = Math.hypot(dx, dy);
        const usable = length - EDGE_HIT_ENDPOINT_GAP_MM * 2;
        if (usable < 1e-6) {
            continue;
        }
        const ux = dx / length;
        const uy = dy / length;
        const midX = (a.x + b.x) / 2;
        const midY = (a.y + b.y) / 2;
        boxes.push({
            key: `hit-${i}`,
            // z below Point (Point sits at ~+scale in world) so corner clicks keep the popover
            position: [midX, midY, 0.5],
            rotation: [0, 0, Math.atan2(uy, ux)],
            args: [usable, EDGE_HIT_WIDTH_MM, 1],
        });
    }
    return boxes;
};

const calcArcPoints = (arcParams, segments = 500) => {
    if (!arcParams) {
        return [];
    }
    return new THREE.EllipseCurve(
        arcParams.x,
        arcParams.y,
        arcParams.radius,
        arcParams.radius,
        arcParams.startAngle,
        arcParams.endAngle,
        arcParams.clockwise,
    )
        .getPoints(segments)
        .map((p) => new THREE.Vector2(p.x, p.y));
};

const Border = ({
                    reaOnly,
                    start,
                    next,
                    prev,
                    editableElement,
                    processingHandle,
                    cutData,
                    prevArc,
                    isGroove,
                    position,
                    scale,
                    edge
                }) => {

    const [hover, setHover] = useState(null)

    const active = (edge?.id === editableElement?.id && editableElement?.type === "editEdge") || ((editableElement?.type === "arc" || editableElement?.type === "addEdge") && editableElement?.id === start?.id)
    const preview = (editableElement?.type === "arc" || editableElement?.type === "round") && editableElement?.pointId === start.id && prevArc

    const points = useMemo(
        () => (start.arcParams
            ? calcArcPoints(start.arcParams)
            : [new THREE.Vector2(start.x, start.y), new THREE.Vector2(next.x, next.y)]),
        [start.arcParams, start.x, start.y, next.x, next.y],
    )

    const hitBoxes = useMemo(() => {
        if (isGroove || reaOnly) {
            return [];
        }
        if (!start.arcParams) {
            return buildHitBoxes(points);
        }
        const sparse = points.filter((_, i) => i % ARC_HIT_STEP === 0 || i === points.length - 1);
        return buildHitBoxes(sparse);
    }, [isGroove, reaOnly, points, start.arcParams])

    const prevLine = preview && calcArcPoints(prevArc)

    const onEdgeClick = !reaOnly && withStopPropagation(() => !isGroove && (edge ? processingHandle("editEdge", edge.id) : processingHandle("addEdge", start.id)))
    const onEdgeOver = () => !reaOnly && !isGroove && setHover(true)
    const onEdgeOut = () => !reaOnly && setHover(false)

    return (
        <Layer layer={2}>
            <mesh
                scale={scale}
                position={position}
                onClick={onEdgeClick}
                onPointerOver={onEdgeOver}
                onPointerOut={onEdgeOut}>
                {cutData && editableElement.type === "cut" && cutData?.pointId === start.id &&
                    <Cut scale={scale} data={{prev, start, next}} cutData={cutData}/>
                }
                <LineElement width={isGroove || reaOnly ? 2 : 5} points={points}
                             color={active || hover ? "#3B8BEC" : edge ? COLORS[edge.orderEdge?.color] && COLORS[edge.orderEdge?.color][400] : "black"}/>
                {preview && <LineElement
                    width={4}
                    points={prevLine}
                    color={COLORS[6][800]}
                    dashSize={0.08 / scale} gapSize={0.08 / scale}/>}
                {hitBoxes.map((box) => (
                    <mesh
                        key={box.key}
                        position={box.position}
                        rotation={box.rotation}
                        onClick={onEdgeClick}
                        onPointerOver={onEdgeOver}
                        onPointerOut={onEdgeOut}
                    >
                        <boxGeometry args={box.args}/>
                        <meshBasicMaterial transparent opacity={0} depthWrite={false} toneMapped={false}/>
                    </mesh>
                ))}
            </mesh>
        </Layer>
    )
}


export {
    Border,
}
